/* global document */

export function escapeHtml(str: string): string {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

export function getExtension(filename: string): string {
  const parts = filename.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface FileIconSpec {
  label: string;
  bodyColor: string;
  foldColor: string;
  textColor: string;
}

const FILE_ICON_SPECS: Record<string, FileIconSpec> = {
  pdf: { label: "PDF", bodyColor: "#d13438", foldColor: "#f3b1b6", textColor: "#ffffff" },
  doc: { label: "DOC", bodyColor: "#185abd", foldColor: "#9cc2ff", textColor: "#ffffff" },
  docx: { label: "DOC", bodyColor: "#185abd", foldColor: "#9cc2ff", textColor: "#ffffff" },
  xls: { label: "XLS", bodyColor: "#107c41", foldColor: "#8fd9b4", textColor: "#ffffff" },
  xlsx: { label: "XLS", bodyColor: "#107c41", foldColor: "#8fd9b4", textColor: "#ffffff" },
  csv: { label: "CSV", bodyColor: "#0f6c3f", foldColor: "#8fd9b4", textColor: "#ffffff" },
  ppt: { label: "PPT", bodyColor: "#c43e1c", foldColor: "#ffbeac", textColor: "#ffffff" },
  pptx: { label: "PPT", bodyColor: "#c43e1c", foldColor: "#ffbeac", textColor: "#ffffff" },
  txt: { label: "TXT", bodyColor: "#5f6a7a", foldColor: "#cfd6dd", textColor: "#ffffff" },
  png: { label: "IMG", bodyColor: "#8e562e", foldColor: "#f2cb9b", textColor: "#ffffff" },
  jpg: { label: "IMG", bodyColor: "#8e562e", foldColor: "#f2cb9b", textColor: "#ffffff" },
  jpeg: { label: "IMG", bodyColor: "#8e562e", foldColor: "#f2cb9b", textColor: "#ffffff" },
  gif: { label: "IMG", bodyColor: "#8e562e", foldColor: "#f2cb9b", textColor: "#ffffff" },
  bmp: { label: "IMG", bodyColor: "#8e562e", foldColor: "#f2cb9b", textColor: "#ffffff" },
  webp: { label: "IMG", bodyColor: "#8e562e", foldColor: "#f2cb9b", textColor: "#ffffff" },
  zip: { label: "ZIP", bodyColor: "#8764b8", foldColor: "#cfbef0", textColor: "#ffffff" },
  rar: { label: "ZIP", bodyColor: "#8764b8", foldColor: "#cfbef0", textColor: "#ffffff" },
  "7z": { label: "ZIP", bodyColor: "#8764b8", foldColor: "#cfbef0", textColor: "#ffffff" },
  eml: { label: "EML", bodyColor: "#0f6cbd", foldColor: "#a7d8ff", textColor: "#ffffff" },
  msg: { label: "MSG", bodyColor: "#0f6cbd", foldColor: "#a7d8ff", textColor: "#ffffff" },
};

function fallbackIconLabel(ext: string): string {
  const safe = ext.replace(/[^a-z0-9]/gi, "").toUpperCase();
  if (!safe) return "FILE";
  return safe.slice(0, 4);
}

function renderFileIconSvg(spec: FileIconSpec): string {
  return `
    <svg class="file-icon-svg" viewBox="0 0 20 24" aria-hidden="true" focusable="false">
      <path d="M3 1.5H12.2L17 6.2V22.5H3V1.5Z" fill="${spec.bodyColor}" />
      <path d="M12.2 1.5V6.2H17Z" fill="${spec.foldColor}" />
      <path d="M3 1.5H12.2L17 6.2V22.5H3V1.5Z" fill="none" stroke="rgba(0,0,0,0.25)" stroke-width="0.9" />
      <text x="10" y="18.2" text-anchor="middle" font-family="Segoe UI, Arial, sans-serif" font-size="5.5" font-weight="700" fill="${spec.textColor}">${spec.label}</text>
    </svg>
  `;
}

export function getFileIcon(ext: string): string {
  const normalized = (ext ?? "").trim().toLowerCase();
  const spec = FILE_ICON_SPECS[normalized] ?? {
    label: fallbackIconLabel(normalized),
    bodyColor: "#605e5c",
    foldColor: "#d2d0ce",
    textColor: "#ffffff",
  };

  return renderFileIconSvg(spec);
}

// "loaded" clears the line rather than leaving a persistent "Ready." message -- once the
// selection has finished loading there's nothing left to report, same as "idle". "loading"
// still shows while a refresh is in progress.
export function setStatus(state: "idle" | "loading" | "loaded"): void {
  const el = document.getElementById("status-bar");
  if (!el) return;
  el.textContent = { idle: "", loading: "Loading...", loaded: "" }[state] ?? "";
}

export function normalizeFileNameForCompare(name: string): string {
  return (name ?? "").trim().toLowerCase();
}

export function normalizeEmlFileName(subject: string): string {
  // eslint-disable-next-line no-control-regex
  const safe = (subject || "email").trim().replace(/[<>:"/\\|?*\u0000-\u001F]/g, "_");
  const base = safe.length > 0 ? safe : "email";
  return /\.eml$/i.test(base) ? base : `${base}.eml`;
}

export function base64ToUint8Array(base64: string): Uint8Array {
  const normalized = base64.replace(/\s/g, "");
  const binary = atob(normalized);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function withRetry<T>(
  operation: () => Promise<T>,
  attempts: number,
  delayMs: number,
  onRetry?: (attempt: number, err: unknown) => void,
): Promise<T> {
  let lastError: unknown = null;
  for (let i = 0; i < attempts; i++) {
    try {
      return await operation();
    } catch (err) {
      lastError = err;
      if (i < attempts - 1) {
        onRetry?.(i + 1, err);
        await wait(delayMs * (i + 1));
      }
    }
  }
  throw lastError ?? new Error("Operation failed.");
}

export function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return String(err);
}

export function showCompanySection(visible: boolean): void {
  const el = document.getElementById("company-section");
  if (el) el.style.display = visible ? "block" : "none";
}

export function showCustomersSection(visible: boolean): void {
  const el = document.getElementById("customers-section");
  if (el) el.style.display = visible ? "flex" : "none";
}

export function showSelectionError(message: string): void {
  const container = document.getElementById("attachments-list");
  if (!container) return;
  container.innerHTML = `<div class="attachment-error">${escapeHtml(message)}</div>`;
}
